import React, { useState, useEffect, useRef } from 'react';
import { User, Eye, MessageSquare, ExternalLink, MoreHorizontal, Edit2, Trash2, ShieldAlert } from 'lucide-react';
import { Heart } from 'lucide-react';
import { NewsItem, Language, User as UserType } from '../../types';
import { getNewsViewCount, getNewsLikes } from '../../services/footballService';
import { fetchNewsComments, toggleNewsLike } from '../../services/newsCommentService';
import { XlevenLogo } from '../Common/XlevenLogo';
import { WeeklyMagazineLogo } from '../Common/WeeklyMagazineLogo';
import { WebtoonLogo } from '../Common/WebtoonLogo';

interface FeedNewsCardProps {
    item: NewsItem;
    onClick: () => void;
    index: number;
    lang: Language;
    user?: UserType | null;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onReport?: (id: string) => void;
}

export const FeedNewsCard: React.FC<FeedNewsCardProps> = React.memo(({ 
    item, onClick, index, lang, user, onEdit, onDelete, onReport 
}) => {
    const [imgError, setImgError] = useState(false);
    const [iconError, setIconError] = useState(false);
    const [views, setViews] = useState<number | null>(null);
    const [commentCount, setCommentCount] = useState<number | null>(null);
    const [likesCount, setLikesCount] = useState<number>(0);
    const [isLiked, setIsLiked] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (cardRef.current) observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;
        let mounted = true;
        const loadStats = async () => {
            try {
                const [v, c, lData] = await Promise.all([
                    getNewsViewCount(item.id),
                    fetchNewsComments(item.id, lang),
                    getNewsLikes(item.id)
                ]);
                if (mounted) {
                    setViews(v);
                    setCommentCount(c.length);
                    setLikesCount(lData.length);
                    if (user) {
                        setIsLiked(lData.some((l: any) => l.user_id === user.id));
                    }
                }
            } catch (e) {
                console.warn("Stats load failed for card:", item.id);
            }
        };
        loadStats();
        return () => { mounted = false; };
    }, [isVisible, item.id, lang]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const handleLike = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user) {
            (window as any).navigateTo?.('/login');
            return;
        }

        // Optimistic Update
        const newIsLiked = !isLiked;
        setIsLiked(newIsLiked);
        setLikesCount(prev => prev + (newIsLiked ? 1 : -1));

        try {
            await toggleNewsLike(item.id);
        } catch (error) {
            console.error("Failed to toggle like:", error);
            // Revert on error
            setIsLiked(!newIsLiked);
            setLikesCount(prev => prev + (!newIsLiked ? 1 : -1));
        }
    };

    const sourceLower = item.source?.toLowerCase() || '';
    const isPartnerSource = sourceLower.includes('bbc') || sourceLower.includes('teamtalk') || sourceLower.includes('si') || sourceLower.includes('sports illustrated');
    const isWeeklyMagazine = sourceLower === 'magazine';
    const isWebtoon = sourceLower === 'webtoon';
    const isUserPost = !!item.user_id;
    const isMine = user && user.id === item.user_id;
    const isAdmin = user?.role === 'admin';

    const displayName = isPartnerSource ? (sourceLower === 'pexels' ? 'PEXELS' : 'XLEVEN') : (isUserPost ? item.author?.name : (item.source || null));
    const hasHeader = displayName || isUserPost;
    
    const displayImage = item.image;
    const displayTitle = item.title;

    return (
        <div 
            ref={cardRef}
            className="group w-full bg-white border-b border-gray-100 transition-all flex gap-3 md:gap-4 p-4 md:p-6 md:hover:bg-gray-50/50 animate-in fade-in slide-in-from-bottom-2"
            style={{ 
                animationDelay: `${(index % 10) * 30}ms`,
                // contentVisibility: isVisible ? 'auto' : 'hidden',
                containIntrinsicSize: '200px'
            }}
        >
            {hasHeader && (
                <div className="shrink-0">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center overflow-hidden border border-gray-100 shadow-sm bg-gray-50`}>
                        {isPartnerSource && sourceLower !== 'pexels' ? (
                            <XlevenLogo theme="light" hideText={true} />
                        ) : isWeeklyMagazine ? (
                            <WeeklyMagazineLogo size={46} />
                        ) : isWebtoon ? (
                            <WebtoonLogo size={46} />
                        ) : isUserPost && item.author?.avatar ? (
                            <img 
                                src={item.author.avatar} 
                                className="w-full h-full object-cover" 
                                alt="" 
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                        const iconContainer = document.createElement('div');
                                        iconContainer.className = "flex items-center justify-center w-full h-full text-slate-300";
                                        iconContainer.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
                                        parent.appendChild(iconContainer);
                                    }
                                }}
                            />
                        ) : !iconError && item.sourceIcon ? (
                            <img src={item.sourceIcon} className="w-full h-full object-cover" alt="" onError={() => setIconError(true)} />
                        ) : (
                            <User size={20} className="text-slate-300" />
                        )}
                    </div>
                </div>
            )}

            <div onClick={onClick} className="flex-1 min-w-0 cursor-pointer group">
                {(hasHeader || isMine || isAdmin) && (
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                            {displayName && (
                                <span className="font-black text-slate-900 text-sm md:text-base truncate md:hover:underline uppercase tracking-tight">
                                    {isWeeklyMagazine ? 'Magazine' : (isWebtoon ? 'Webtoon' : displayName)}
                                </span>
                            )}
                            {hasHeader && <span className="text-slate-400 text-xs font-medium shrink-0">·</span>}
                            <span className="text-slate-400 text-xs font-medium shrink-0 uppercase">{item.time}</span>
                        </div>

                        <div className="relative" ref={menuRef}>
                            <button 
                                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                                className="p-1 text-slate-300 md:hover:text-slate-900 transition-all rounded-full md:hover:bg-white active:scale-90"
                            >
                                <MoreHorizontal size={18} />
                            </button>
                            {menuOpen && (
                                <div className="absolute top-full right-0 mt-1 w-40 bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                                    <div className="p-1 flex flex-col gap-0.5">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onReport?.(item.id); }}
                                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 md:hover:bg-slate-50"
                                        >
                                            <ShieldAlert size={14} className="text-rose-500" />
                                            <span>{lang === 'ko' ? '신고하기' : 'Report'}</span>
                                        </button>
                                        {(isMine || isAdmin) && (
                                            <>
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit?.(item.id); }}
                                                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 md:hover:bg-slate-50"
                                                >
                                                    <Edit2 size={14} className="text-indigo-500" />
                                                    <span>{lang === 'ko' ? '수정' : 'Edit'}</span>
                                                </button>
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete?.(item.id); }}
                                                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-rose-500 md:hover:bg-rose-50"
                                                >
                                                    <Trash2 size={14} />
                                                    <span>{lang === 'ko' ? '삭제' : 'Delete'}</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="mb-3">
                    <p className={`leading-relaxed mb-2 text-slate-700 text-md md:text-lg font-medium whitespace-pre-wrap line-clamp-2`}>
                        {displayTitle}
                    </p>
                </div>

                {displayImage && !imgError && (
                    <div className="relative mt-3 mb-4 rounded-2xl md:rounded-[24px] overflow-hidden border border-gray-100 aspect-video bg-gray-50">
                        <img 
                            src={displayImage} 
                            alt="" 
                            className="w-full h-full object-cover transition-transform duration-700 md:group-hover:scale-[1.02]" 
                            onError={() => setImgError(true)} 
                            loading="lazy"
                        />
                        {(isPartnerSource || isWeeklyMagazine) && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (item.url) window.open(item.url, '_blank');
                                }}
                                className="absolute bottom-3 right-3 backdrop-blur-md px-3 py-1.5 rounded-xl text-[9px] font-black text-white uppercase flex items-center gap-1.5 bg-rose-500 transition-all border border-white/10 shadow-lg"
                            >
                                <span className="opacity-70">Source:</span> {item.source === 'magazine' ? 'openai' : item.source || 'INTEL'}
                                <ExternalLink size={10} strokeWidth={3} />
                            </button>
                        )}
                    </div>
                )}

                <div className="flex items-center gap-6 text-slate-400">
                    <button 
                        onClick={handleLike}
                        className={`flex items-center gap-1.5 transition-colors active:scale-90 ${isLiked ? 'text-rose-500' : 'md:hover:text-rose-500'}`}
                    >
                        <Heart size={16} className={isLiked ? 'fill-rose-500' : ''} />
                        <span className="text-[11px] font-black tabular-nums">{likesCount.toLocaleString()}</span>
                    </button>
                    <div className="flex items-center gap-1.5 md:hover:text-indigo-500 transition-colors">
                        <MessageSquare size={15} />
                        <span className="text-[11px] font-black tabular-nums">{commentCount === null ? '...' : commentCount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Eye size={16} />
                        <span className="text-[11px] font-black tabular-nums">{views === null ? '...' : views.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.item.id === nextProps.item.id &&
        prevProps.lang === nextProps.lang &&
        prevProps.user?.id === nextProps.user?.id &&
        prevProps.index === nextProps.index
    );
});

FeedNewsCard.displayName = 'FeedNewsCard';
